import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Spinner } from 'common/spinners';
import {
  canAddMoreGroups,
} from './utils';
import './stylesheets/referral-groups-programs.scss';
import computeFieldErrors from './utils/computeFieldErrors';
import syncFieldArray from './utils/syncFieldArray';
import useFieldCallbacks from './utils/useFieldCallbacks';
import renderFieldRow from './utils/renderFieldRow';
import { renderServiceTypeSection, renderAuthRequiredMessage, renderAddButton } from './utils/renderSections';

const ReferralGroupsPrograms = (props) => {
  const {
    allowEmptyGroups,
    canPaginateNetworkGroups,
    caseReferrals,
    ccGroupIds,
    currentUserGroup,
    debouncedSearchNetworkGroups,
    feeScheduleProgramId,
    groupKey,
    hide,
    index: componentIndex,
    isFetchingNetworkGroups,
    isFetchingOONGroups,
    loading,
    originCoordinates,
    programKey,
    referral,
    removeSelectedBrowseGroup,
    selectedFields,
    selectedProgramsContext,
    serviceAreaSupportForOrgsFlag,
    serviceTypeField,
    setFeeScheduleProgramId,
    setHasReferralProgramSelect,
    setHasSensitiveErrors,
    suggestedGroups,
    toggleBrowse,
    width,
  } = props;

  const showOrgAddButton = suggestedGroups.length > 0 && canAddMoreGroups({ selectedFields, suggestedGroups });
  const disableOrgAddButton = _.some(_.compact(selectedFields), ['group.value', '']);

  const {
    checkSensitiveErrors,
    onProgramGroupSelect,
    addGroupField,
  } = useFieldCallbacks({
    canPaginateNetworkGroups,
    caseReferrals,
    componentIndex,
    currentUserGroup,
    feeScheduleProgramId,
    groupKey,
    referral,
    removeSelectedBrowseGroup,
    selectedFields,
    selectedProgramsContext,
    setFeeScheduleProgramId,
    setHasSensitiveErrors,
    suggestedGroups,
  });

  useEffect(() => {
    syncFieldArray(selectedFields, suggestedGroups);
    checkSensitiveErrors();
  }, [selectedFields, suggestedGroups, checkSensitiveErrors]);

  useEffect(() => {
    setHasReferralProgramSelect(false);
  }, [setHasReferralProgramSelect]);

  if (loading && !isFetchingOONGroups) {
    return (
      <div className="service-type-section row">
        <div className="col-xs-12">
          <Spinner scale={0.7} />
        </div>
      </div>
    );
  }

  const { errorsByField, sensitiveErrorsByField, sensitiveErrorKey } = computeFieldErrors({
    selectedFields,
    groupKey,
    caseReferrals,
    ccGroupIds,
    referral,
    suggestedGroups,
    currentUserGroup,
  });

  const groupProgramFields = selectedFields.map((selected, index) => (
    renderFieldRow({
      allowEmptyGroups,
      debouncedSearchNetworkGroups,
      errorsByField,
      groupKey,
      hide,
      index,
      onProgramGroupSelect,
      originCoordinates,
      programKey,
      referral,
      selected,
      selectedFields,
      sensitiveErrorsByField,
      sensitiveErrorKey,
      serviceAreaSupportForOrgsFlag,
      suggestedGroups,
      width,
    })
  ));

  return (
    <div className="referral-groups-programs">
      <div className="row">
        <div className="col-xs-12">
          {renderServiceTypeSection(serviceTypeField, toggleBrowse, isFetchingNetworkGroups)}
        </div>
      </div>

      {groupProgramFields}

      {renderAuthRequiredMessage(feeScheduleProgramId, showOrgAddButton)}

      {renderAddButton({
        showOrgAddButton, hide, disableOrgAddButton, addGroupField,
      })}
    </div>
  );
};

ReferralGroupsPrograms.propTypes = {
  allowEmptyGroups: PropTypes.bool,
  caseReferrals: PropTypes.array,
  canPaginateNetworkGroups: PropTypes.bool.isRequired,
  ccGroupIds: PropTypes.array,
  feeScheduleProgramId: PropTypes.string,
  groupKey: PropTypes.string,
  hide: PropTypes.bool,
  index: PropTypes.number.isRequired,
  isFetchingOONGroups: PropTypes.bool,
  loading: PropTypes.bool,
  debouncedSearchNetworkGroups: PropTypes.func.isRequired,
  programKey: PropTypes.string,
  referral: PropTypes.object.isRequired,
  originCoordinates: PropTypes.array.isRequired,
  removeSelectedBrowseGroup: PropTypes.func.isRequired,
  selectedFields: PropTypes.array.isRequired,
  serviceTypeField: PropTypes.object.isRequired,
  suggestedGroups: PropTypes.array.isRequired,
  toggleBrowse: PropTypes.func.isRequired,
  serviceAreaSupportForOrgsFlag: PropTypes.bool,
  width: PropTypes.number,
  currentUserGroup: PropTypes.object.isRequired,
  setHasSensitiveErrors: PropTypes.func.isRequired,
  setFeeScheduleProgramId: PropTypes.func,
  setHasReferralProgramSelect: PropTypes.func,
  selectedProgramsContext: PropTypes.shape({
    selectedPrograms: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })).isRequired,
    dispatchRemoveAllPrograms: PropTypes.func.isRequired,
    dispatchAddProgram: PropTypes.func.isRequired,
    dispatchRemoveProgram: PropTypes.func.isRequired,
  }).isRequired,
  isFetchingNetworkGroups: PropTypes.bool,
};

ReferralGroupsPrograms.defaultProps = {
  allowEmptyGroups: false,
  caseReferrals: [],
  ccGroupIds: [],
  feeScheduleProgramId: null,
  groupKey: 'group',
  hide: true,
  isFetchingOONGroups: false,
  loading: true,
  programKey: 'program',
  serviceAreaSupportForOrgsFlag: false,
  width: undefined,
  setFeeScheduleProgramId: () => { },
  setHasReferralProgramSelect: () => { },
  isFetchingNetworkGroups: false,
};

export default ReferralGroupsPrograms;
